/**
 * Centralized API configuration
 * Ensures all API calls use the correct URL in both development and production
 */
import { getActingOrganizationHeaders } from './actingOrganization';
import { clientBuildHeaders, isClientOutdatedBody, notifyClientOutdated } from './clientBuild';
import {
  isFeatureDisabledForOrganization,
  onboardingPathForOrganization403,
} from './onboardingRedirect';
import { confirmLiveWrite } from './impersonationWriteGate';

export const getApiUrl = (): string => {
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';

  // Always use local proxy in development
  // Check both NODE_ENV and hostname to be extra safe
  const isDevelopment = 
    process.env.NODE_ENV === 'development' || 
    (typeof window !== 'undefined' && (
      hostname === 'localhost' || 
      hostname === '127.0.0.1' ||
      hostname.includes('localhost')
    ));
  
  if (isDevelopment) {
    // In development, always use the proxy
    // The proxy at /api forwards to http://localhost:3001
    return '/api';
  }

  // In browser production, default to same-origin API path to avoid cross-origin CORS issues.
  // Firebase Hosting rewrite maps /api/** to the API function.
  // Set REACT_APP_USE_ABSOLUTE_API_URL=true to force absolute URL behavior.
  if (typeof window !== 'undefined') {
    const useAbsoluteApiUrl = process.env.REACT_APP_USE_ABSOLUTE_API_URL === 'true';
    if (!useAbsoluteApiUrl) {
      return '/api';
    }
  }

  // For server-side/non-browser contexts, allow explicit override if configured.
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Production: Use api.motzulabs.com subdomain
  // This subdomain routes directly to the Firebase function
  // No /api prefix needed - the subdomain handles routing
  return 'https://api.motzulabs.com';
};

const normalizeHeaders = (input?: HeadersInit): Record<string, string> => {
  const headers: Record<string, string> = {};
  if (!input) return headers;

  if (input instanceof Headers) {
    input.forEach((value, key) => {
      headers[key] = value;
    });
  } else if (Array.isArray(input)) {
    input.forEach(([key, value]) => {
      headers[key] = value as string;
    });
  } else {
    Object.assign(headers, input as Record<string, string>);
  }

  return headers;
};

export const makeApiCall = async (
  endpoint: string,
  options: RequestInit = {},
  allowRetry: boolean = true
): Promise<Response> => {
  // While an admin is viewing as another account, a mutating request waits for
  // their confirmation (MOT-169). Only the first attempt asks: the internal
  // retries below pass allowRetry=false and ride on that confirmation.
  if (allowRetry) {
    await confirmLiveWrite(options.method, endpoint);
  }

  // Ensure endpoint starts with /
  const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${getApiUrl()}${formattedEndpoint}`;
  
  // Add cache-busting headers to prevent 504 timeout issues
  const headersObj: Record<string, string> = {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
    ...clientBuildHeaders(),
  };
  // Merge any provided headers into a plain object safely (HeadersInit can be many shapes)
  const originalHeaders = normalizeHeaders(options.headers);
  Object.assign(headersObj, originalHeaders);

  // Multipart uploads: callers use getAuthHeaders() which sets Content-Type: application/json.
  // For FormData the browser must set multipart/form-data with a boundary — wrong Content-Type breaks multer.
  if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
    delete headersObj['Content-Type'];
    delete headersObj['content-type'];
  }

  // Keep both X-Session-ID and Authorization headers
  // Backend will try session ID first, then fall back to JWT if session is invalid
  // This allows seamless fallback when session expires or server restarts

  const response = await fetch(url, {
    ...options,
    headers: headersObj,
  });

  // The server says this build is below its floor: put the app in `required`.
  if (response.status === 409) {
    try {
      if (isClientOutdatedBody(await response.clone().json())) notifyClientOutdated();
    } catch {
      // Not JSON: an ordinary 409 for the caller to handle.
    }
  }

  if (response.status === 401 && allowRetry) {
    try {
      const { auth } = await import('../firebase');
      const currentUser = auth.currentUser;
      if (currentUser) {
        // Clear cached session so we get a fresh one
        localStorage.removeItem('api_session_id');
        localStorage.removeItem('api_session_expires');
        // Force refresh Firebase token
        await currentUser.getIdToken(true);
        const refreshedHeaders = await getAuthHeaders(true);
        const mergedHeaders = { ...originalHeaders, ...refreshedHeaders };
        if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
          delete mergedHeaders['Content-Type'];
          delete mergedHeaders['content-type'];
        }

        return makeApiCall(
          endpoint,
          {
            ...options,
            headers: mergedHeaders,
          },
          false
        );
      }
    } catch (refreshError) {
      console.warn('Failed to refresh auth token after 401:', refreshError);
    }
  }
  
  // Handle 403 errors related to missing organization - redirect to onboarding.
  // Platform admins often have no org/userType; after exiting impersonation,
  // in-flight store/org calls 403 and must NOT send them to select-type.
  if (response.status === 403) {
    try {
      const errorData = await response.clone().json();
      const errorCode = errorData?.error?.code;
      const errorMessage = errorData?.error?.message || errorData?.message || '';

      // The ID token keeps email_verified from when it was issued. Someone who
      // already opened the link still looks unverified until the user record
      // is reloaded and the token is forced fresh. Retry once after that.
      // If the address is still unverified, send them to the verify page,
      // which names the inbox, instead of leaving a modal stuck on this 403.
      if (errorCode === 'email_not_verified') {
        if (allowRetry) {
          try {
            const { auth } = await import('../firebase');
            const currentUser = auth.currentUser;
            if (currentUser) {
              localStorage.removeItem('api_session_id');
              localStorage.removeItem('api_session_expires');
              await currentUser.reload();
              await currentUser.getIdToken(true);
              if (currentUser.emailVerified) {
                const refreshedHeaders = await getAuthHeaders(true);
                const mergedHeaders = { ...originalHeaders, ...refreshedHeaders };
                if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
                  delete mergedHeaders['Content-Type'];
                  delete mergedHeaders['content-type'];
                }
                return makeApiCall(
                  endpoint,
                  { ...options, headers: mergedHeaders },
                  false
                );
              }
            }
          } catch (refreshError) {
            console.warn('Failed to refresh auth token after email verification:', refreshError);
          }
        }
        if (
          typeof window !== 'undefined' &&
          !window.location.pathname.startsWith('/verify-email')
        ) {
          window.location.assign('/verify-email');
        }
        return response;
      }

      // A store under review gets 403s by design (explore mode); the shell
      // banner explains it, so never bounce them to onboarding. A dark
      // feature says "not enabled for your organization" — same idea, and
      // Store Settings hits that on purpose for groups.
      if (
        errorData?.error?.code === 'organization_pending_approval' ||
        isFeatureDisabledForOrganization(errorMessage)
      ) {
        return response;
      }

      if (
        (errorMessage.includes('organization') || errorMessage.includes('onboarding')) &&
        !window.location.pathname.startsWith('/onboarding')
      ) {
        const { getAuthHeaders } = await import('./apiConfig');
        const headers = await getAuthHeaders();

        try {
          const statusResponse = await fetch(`${getApiUrl()}/onboarding/status`, { headers });
          if (!statusResponse.ok) {
            console.warn('⚠️ Onboarding status check failed after 403 — not redirecting');
            return response;
          }

          const statusData = await statusResponse.json();
          let isPlatformAdmin = statusData.data?.isAdmin === true;
          if (!isPlatformAdmin) {
            try {
              const { auth } = await import('../firebase');
              const token = await auth.currentUser?.getIdTokenResult();
              const claimRole = token?.claims?.role;
              isPlatformAdmin = claimRole === 'admin' || claimRole === 'super_admin';
            } catch {
              // ignore claim lookup failures
            }
          }

          const redirectPath = onboardingPathForOrganization403(errorMessage, errorData?.error?.code, {
            ...statusData.data,
            isAdmin: isPlatformAdmin,
          });
          if (!redirectPath) {
            return response;
          }

          console.log('⚠️ User missing organization, redirecting to onboarding...');
          window.location.href = redirectPath;
        } catch {
          console.warn('⚠️ Failed to resolve onboarding redirect after 403 — not redirecting');
        }
      }
    } catch {
      // If error parsing fails, continue with normal error handling
    }
  }
  
  return response;
};

/**
 * Parse a JSON API response without ever surfacing the parser's own message.
 *
 * A gateway answers a function that ran out of memory or time with an HTML page
 * (502/503/504), and `response.json()` on that throws `Unexpected token '<', "<!DOCTYPE"...`.
 * Callers keep their own `!response.ok` handling on the parsed body; this only turns a body
 * that is not JSON into a message a person can act on. Returns the parsed body whatever the
 * status, so callers that inspect an error body still can.
 */
export const readJsonResponse = async <T = any>(response: Response, fallbackMessage: string): Promise<T> => {
  const contentType = response.headers?.get?.('content-type') || '';
  try {
    if (contentType && !/json/i.test(contentType)) throw new Error('not json');
    return (await response.json()) as T;
  } catch {
    throw new Error(
      response.ok
        ? fallbackMessage
        : `The server did not finish the request (HTTP ${response.status}). Try again in a minute.`,
    );
  }
};

export const getAuthHeaders = async (forceRefresh: boolean = false): Promise<Record<string, string>> => {
  const { auth } = await import('../firebase');
  
  // Check if user is authenticated first
  const user = auth.currentUser;
  if (!user) {
    console.error('❌ No authenticated user found in getAuthHeaders');
    throw new Error('User not authenticated. Please log in.');
  }

  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const useSession =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.local');

  // Try to get existing session ID from localStorage (skip if forced refresh or not using session auth)
  let sessionId = useSession && !forceRefresh
    ? localStorage.getItem('api_session_id')
    : null;
  
  // Check if session is expired first
  if (useSession && sessionId) {
    const expires = localStorage.getItem('api_session_expires');
    if (expires && parseInt(expires) < Date.now()) {
      localStorage.removeItem('api_session_id');
      localStorage.removeItem('api_session_expires');
      sessionId = null;
    }
  }
  
  // If no session ID, use JWT directly (don't block on session creation)
  // Session will be created lazily in the background for subsequent requests
  if (useSession && !sessionId) {
    const token = await user.getIdToken(forceRefresh);
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    // Create session in background (non-blocking) for future requests
    fetch(`${getApiUrl()}/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    }).then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        const newSessionId = data.data?.sessionId;
        if (newSessionId) {
          localStorage.setItem('api_session_id', newSessionId);
          localStorage.setItem('api_session_expires', (Date.now() + (data.data.expiresIn * 1000)).toString());
        }
      }
    }).catch(() => {
      // Silently fail - JWT will continue to work
    });
    
    // Return JWT headers immediately (don't wait for session)
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...getImpersonationHeaders(),
      ...getActingOrganizationHeaders(user.uid),
    };
  }
  
  // Always get JWT token as fallback (even if we have a session ID)
  const token = await user.getIdToken(forceRefresh);
  if (!token) {
    console.error('❌ Failed to get JWT token');
    throw new Error('No authentication token available');
  }
  
  // Use session ID as primary, but always include JWT token as fallback
  // This way if the session is invalid/expired on the backend, it can use JWT
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...getImpersonationHeaders(),
    ...getActingOrganizationHeaders(user.uid),
  };

  if (useSession && sessionId) {
    headers['X-Session-ID'] = sessionId;
  }

  return headers;
};

/** Admin impersonation context from localStorage (store/vendor org/brand). */
function getImpersonationHeaders(): Record<string, string> {
  if (typeof window === 'undefined' || !window.localStorage) return {};
  try {
    const impersonationData = window.localStorage.getItem('admin-impersonation');
    if (!impersonationData) return {};
    const parsed = JSON.parse(impersonationData);
    if (!parsed?.isImpersonating) return {};
    const headers: Record<string, string> = {};
    if (parsed?.org?.id) {
      headers['X-Impersonation-Org-Id'] = parsed.org.id;
      headers['X-Impersonated-Organization-Id'] = parsed.org.id;
    }
    if (parsed?.brand?.id) {
      headers['X-Impersonation-Brand-Id'] = parsed.brand.id;
      headers['X-Impersonated-Brand-Id'] = parsed.brand.id;
    }
    if (parsed?.role) {
      headers['X-Impersonation-Role'] = parsed.role;
      headers['X-Impersonated-Role'] = parsed.role;
    }
    if (parsed?.userType || parsed?.impersonatedUserType) {
      const userType = parsed?.userType || parsed?.impersonatedUserType;
      headers['X-Impersonation-User-Type'] = userType;
      headers['X-Impersonated-User-Type'] = userType;
    }
    return headers;
  } catch (error) {
    console.warn('Failed to parse impersonation data for headers:', error);
    return {};
  }
}

/**
 * The impersonation half of a "who is acting right now" identity key, for
 * page-lifetime caches that must not survive an admin entering or leaving
 * impersonation (org and product stay the same; only what data comes back
 * changes). Reads the same `admin-impersonation` localStorage entry
 * `getImpersonationHeaders` above sends as `X-Impersonation-*` headers, so a
 * cache keyed on this plus the signed-in user's uid (from `useAuth()`,
 * where a component already has it) invalidates on both an org switch
 * between a multi-org member's memberships and an impersonation change.
 * This branch exposes no separate "active organization" concept beyond
 * those two, so uid + this covers every acting-identity switch the app can
 * make. Synchronous and side-effect free so it can be read directly in a
 * render or a query key.
 */
export function getImpersonationIdentityKey(): string {
  if (typeof window === 'undefined' || !window.localStorage) return 'none';
  try {
    const impersonationData = window.localStorage.getItem('admin-impersonation');
    if (!impersonationData) return 'none';
    const parsed = JSON.parse(impersonationData);
    if (!parsed?.isImpersonating) return 'none';
    const key = [parsed?.org?.id, parsed?.brand?.id, parsed?.userType || parsed?.impersonatedUserType]
      .filter(Boolean)
      .join(':');
    return key || 'active';
  } catch {
    return 'none';
  }
}
